import { getAsyncDuplicationWorkflowStatus, getWorkflowStatus } from '@goldcast/api/backend';
import logger from '@goldcast/logger';

enum WorkflowStatus {
  COMPLETED = 'COMPLETED',
  RUNNING = 'RUNNING'
}

export enum ActionType {
  DUPLICATION = 'duplication',
  DELETION = 'deletion',
  SESSION_DELETION = 'session_deletion',
  POLL_ADDITION = 'poll_addition',
  POLL_UPDATION = 'poll_updation'
}

const getActionMessage = (actionType: ActionType): string => {
  const ACTION_MESSAGES = {
    [ActionType.DUPLICATION]: 'Event duplication',
    [ActionType.DELETION]: 'Event deletion',
    [ActionType.SESSION_DELETION]: 'Session deletion',
    [ActionType.POLL_ADDITION]: 'Poll addition',
    [ActionType.POLL_UPDATION]: 'Poll updation'
  };
  return ACTION_MESSAGES[actionType as keyof typeof ACTION_MESSAGES];
};

const isWorkflowReady = (status: WorkflowStatus): boolean => {
  return status !== WorkflowStatus.RUNNING;
};

const determineOverallStatus = (parentStatus: WorkflowStatus, childStatus: WorkflowStatus): WorkflowStatus => {
  const parentReady = isWorkflowReady(parentStatus);
  const childReady = isWorkflowReady(childStatus);

  return parentReady && childReady ? WorkflowStatus.COMPLETED : WorkflowStatus.RUNNING;
};

const actions = {
  async checkAsyncDuplicationStatus(
    context,
    { eventId, checkParentOnly }: { eventId: string; checkParentOnly: boolean }
  ) {
    try {
      const parentResponse = await getAsyncDuplicationWorkflowStatus({
        event_id: eventId
      });

      const parentStatus = parentResponse.status;

      if (checkParentOnly) {
        return { parentStatus };
      }

      const [childResponseSync, childResponseAsync] = await Promise.all([
        getWorkflowStatus({
          workflow_id: 'event-duplication-' + eventId
        }),
        getWorkflowStatus({
          workflow_id: 'async-duplication-' + eventId
        })
      ]);

      const childStatus = determineOverallStatus(
        childResponseSync.status as WorkflowStatus,
        childResponseAsync.status as WorkflowStatus
      );

      return { parentStatus, childStatus };
    } catch (error) {
      logger.error('Error checking async duplication status:', error);
      throw error;
    }
  },
  async validateActionAllowed(
    context,
    { actionType, eventId, checkParentOnly }: { actionType: ActionType; eventId: string; checkParentOnly: boolean }
  ) {
    const enableDuplicateEventViaWorkflow = context.rootState.org?.currentOrg?.enableDuplicateEventViaWorkflow;

    if (!enableDuplicateEventViaWorkflow) {
      return true;
    }

    try {
      const statusResult = await context.dispatch('checkAsyncDuplicationStatus', { eventId, checkParentOnly });

      const status = checkParentOnly
        ? statusResult.parentStatus
        : determineOverallStatus(statusResult.parentStatus, statusResult.childStatus!);

      if (isWorkflowReady(status)) {
        return true;
      }

      const actionName = getActionMessage(actionType);
      const actionMessage = `${actionName} couldn't be initiated. The event is still getting ready. Please try again later.`;

      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: actionMessage,
          mode: 'error'
        },
        { root: true }
      );

      return false;
    } catch (error) {
      logger.error('Error validating action:', error);
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'Unable to verify if action is allowed. Please try again later.',
          mode: 'error'
        },
        { root: true }
      );
      return false;
    }
  }
};

export default {
  actions,
  namespaced: true
};
